import { useContext, useState } from 'react'

import { useCallMutation } from '@/common/api/client'
import { apiPutFetcher } from '@/common/api/fetcher'
import { ApiPath } from '@/common/api/path'
import { Site } from '@/common/model/site'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import CheckBox, { CheckBoxType } from '@/components/view/atoms/checkbox/CheckBox'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'

import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'
import { MessageType } from '@/components/view/molecules/bar/MessageBar'

const inputCls =
  'shadow-inset text-size-13 rounded-lg border border-border-input bg-input px-3 py-2 w-full'

interface Props {
  site: Site
  onRefetch?: () => void
}

// サイト管理: FTP の下に置く「API連携（W2 OpenAPI 送信）」設定
const SiteFormApiInput = ({ site, onRefetch }: Props) => {
  const { addMessage } = useContext(ToastMessageContext)
  const { trigger, isMutating } = useCallMutation(
    ApiPath.UPDATE_SITE,
    {
      onSuccess: () => {
        addMessage({ text: 'API連携設定を保存しました', type: MessageType.SUCCESS })
        onRefetch && onRefetch()
      }
    },
    apiPutFetcher
  )

  const [d, setD] = useState({
    enable_w2_send: !!site.enable_w2_send,
    enable_target_list: !!site.enable_target_list,
    w2_api_key: site.w2_api_key ?? '',
    w2_api_base_url: site.w2_api_base_url ?? '',
    w2_basic_user: site.w2_basic_user ?? '',
    w2_basic_pass: site.w2_basic_pass ?? '',
    w2_rate_limit_per_min: site.w2_rate_limit_per_min ?? 60,
    w2_retry_interval_sec: site.w2_retry_interval_sec ?? 5,
    w2_retry_max: site.w2_retry_max ?? 3,
    w2_auto_send_enabled: !!site.w2_auto_send_enabled,
    w2_auto_send_start_hour: site.w2_auto_send_start_hour ?? 0,
    w2_auto_send_interval_hours: site.w2_auto_send_interval_hours ?? 24
  })
  const set = <K extends keyof typeof d>(k: K, v: (typeof d)[K]) => setD((s) => ({ ...s, [k]: v }))

  const save = () => trigger({ site_id: site.id, ...d })

  return (
    <Area>
      <AreaContent>
        <Text weight='bold'>API連携（W2 OpenAPI 送信）</Text>
        <div className='mt-1'>
          <Text size={12} color={TextColor.SECONDARY}>
            ターゲットリストなどを W2Commerce へ送信するための連携設定です。
          </Text>
        </div>

        <div className='mt-3 flex flex-col gap-3'>
          <FormRow align='center' gap={12}>
            <FormLabel fixWidth={176}>W2Commerce送信設定</FormLabel>
            <CheckBox
              name='enable_w2_send'
              type={CheckBoxType.TOGGLE_TEXT}
              toggleText={{ left: 'ON', right: 'OFF' }}
              checked={d.enable_w2_send}
              onChange={(v) => set('enable_w2_send', v)}
            />
          </FormRow>
          <FormRow align='center' gap={12}>
            <FormLabel fixWidth={176}>ターゲットリスト連携</FormLabel>
            <CheckBox
              name='enable_target_list'
              type={CheckBoxType.TOGGLE_TEXT}
              toggleText={{ left: 'ON', right: 'OFF' }}
              checked={d.enable_target_list}
              onChange={(v) => set('enable_target_list', v)}
            />
          </FormRow>

          <div>
            <FormLabel>APIキー</FormLabel>
            <input
              className={inputCls}
              value={d.w2_api_key}
              placeholder='X-api-key'
              onChange={(e) => set('w2_api_key', e.target.value)}
            />
          </div>
          <div>
            <FormLabel>環境URL</FormLabel>
            <input
              className={inputCls}
              value={d.w2_api_base_url}
              placeholder='https://example.com/w2-open-api/v1'
              onChange={(e) => set('w2_api_base_url', e.target.value)}
            />
          </div>

          <div>
            <FormLabel>Basic認証ID</FormLabel>
            <input
              className={inputCls}
              value={d.w2_basic_user}
              placeholder='検証環境のBasic認証ユーザー名（不要なら空欄）'
              autoComplete='off'
              onChange={(e) => set('w2_basic_user', e.target.value)}
            />
          </div>
          <div>
            <FormLabel>Basic認証パスワード</FormLabel>
            <input
              type='password'
              className={inputCls}
              value={d.w2_basic_pass}
              placeholder='検証環境のBasic認証パスワード（不要なら空欄）'
              autoComplete='new-password'
              onChange={(e) => set('w2_basic_pass', e.target.value)}
            />
          </div>

          <div>
            <FormLabel>毎分上限値</FormLabel>
            <input
              type='number'
              min={1}
              className={`${inputCls} !w-24`}
              value={d.w2_rate_limit_per_min}
              onChange={(e) => set('w2_rate_limit_per_min', Number(e.target.value))}
            />
          </div>
          <div>
            <FormLabel>リトライ間隔 / 最大回数</FormLabel>
            <div className='mt-1 flex items-center gap-1'>
              <input
                type='number'
                min={0}
                className={`${inputCls} !w-16`}
                value={d.w2_retry_interval_sec}
                onChange={(e) => set('w2_retry_interval_sec', Number(e.target.value))}
              />
              <Text size={12} color={TextColor.SECONDARY}>
                秒
              </Text>
              <input
                type='number'
                min={0}
                className={`${inputCls} !w-16`}
                value={d.w2_retry_max}
                onChange={(e) => set('w2_retry_max', Number(e.target.value))}
              />
              <Text size={12} color={TextColor.SECONDARY}>
                回
              </Text>
            </div>
          </div>

          <FormRow align='center' gap={12}>
            <FormLabel fixWidth={176}>自動送信</FormLabel>
            <CheckBox
              name='w2_auto_send_enabled'
              type={CheckBoxType.TOGGLE_TEXT}
              toggleText={{ left: 'ON', right: 'OFF' }}
              checked={d.w2_auto_send_enabled}
              onChange={(v) => set('w2_auto_send_enabled', v)}
            />
          </FormRow>
          {d.w2_auto_send_enabled && (
            <div>
              <FormLabel>実施タイミング</FormLabel>
              <div className='mt-1 flex items-center gap-1'>
                <input
                  type='number'
                  min={0}
                  max={23}
                  className={`${inputCls} !w-16`}
                  value={d.w2_auto_send_start_hour}
                  onChange={(e) => set('w2_auto_send_start_hour', Number(e.target.value))}
                />
                <Text size={12} color={TextColor.SECONDARY}>
                  時から
                </Text>
                <input
                  type='number'
                  min={1}
                  className={`${inputCls} !w-16`}
                  value={d.w2_auto_send_interval_hours}
                  onChange={(e) => set('w2_auto_send_interval_hours', Number(e.target.value))}
                />
                <Text size={12} color={TextColor.SECONDARY}>
                  時間置き
                </Text>
              </div>
            </div>
          )}

          <div className='pt-1'>
            <Button color={ButtonColor.PRIMARY} onClick={save} loading={isMutating}>
              API連携設定を保存
            </Button>
          </div>
        </div>
      </AreaContent>
    </Area>
  )
}

export default SiteFormApiInput
